import type React from "react";
import { useState, useCallback } from "react";

import { Button } from "reactstrap";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { PreviewModal } from "./PreviewModal";

interface ImageThumbnailProps {
  previewUrl: string | null;
  altText: string;
  onDelete?: () => void;
  disabled?: boolean;
  showDeleteButton?: boolean;
}

export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  previewUrl,
  altText,
  onDelete,
  disabled = false,
  showDeleteButton = false,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const togglePreviewModal = useCallback(() => {
    setPreviewModalOpen((prev) => !prev);
  }, []);

  if (!previewUrl) return null;

  return (
    <>
      <PreviewModal
        isOpen={previewModalOpen}
        toggle={togglePreviewModal}
        previewUrl={previewUrl}
      />

      <div className="position-relative d-flex" style={{ flex: "0 1 4em" }}>
        <button
          type="button"
          className="p-0 rounded border ratio ratio-1x1"
          onClick={togglePreviewModal}
          disabled={disabled}
          aria-label={`View full size ${altText}`}
          style={{
            overflow: "hidden",
            aspectRatio: "1/1",
            maxWidth: "100%",
            maxHeight: "100%",
          }}
        >
          <img
            src={previewUrl}
            alt={`Preview of ${altText}`}
            className="rounded"
            style={{
              objectFit: "cover",
            }}
          />
        </button>

        {showDeleteButton && onDelete && (
          <Button
            color="danger"
            className="position-absolute top-0 end-0 p-0 rounded-circle ratio ratio-1x1 bg-body-secondary"
            outline
            style={{
              maxHeight: "1em",
              maxWidth: "1em",
              margin: "0.1em",
            }}
            onClick={onDelete}
            disabled={disabled}
            aria-label={`Remove ${altText}`}
          >
            <XMarkIcon height={12} width={12} />
          </Button>
        )}
      </div>
    </>
  );
};
